import React, { useEffect, useRef, useState } from 'react'
import { mapsService, ParkingSpot, UserLocation } from '../../services/mapsServiceNew'
import { LocationPermissionModal } from '../Location/LocationPermissionModal'
import { useLocalization } from '../../contexts/LocalizationContext'
import { useAudit } from '../../hooks/useAudit'
import { ParkedVehicle } from '../../lib/supabase'
import { GeolocationError } from '../../services/mapProviders/IMapService'
import { getFallbackLocation } from '../../utils/localeFallback'

interface MapComponentProps {
  onParkingSpotsFound: (spots: ParkingSpot[]) => void
  selectedSpot: ParkingSpot | null
  onMarkerClick?: (spot: ParkingSpot) => void
  parkedVehicle?: ParkedVehicle | null
  onMapClick?: (location: UserLocation | null) => void
  selectedParkingLocation?: UserLocation | null
}

export function MapComponent({ onParkingSpotsFound, selectedSpot, onMarkerClick, parkedVehicle, onMapClick, selectedParkingLocation }: MapComponentProps) {
  const LOCATION_PERMISSION_STORAGE_KEY = 'location_permission_granted'
  const { t } = useLocalization()
  const { logPremiumMapUsage } = useAudit()
  const mapRef = useRef<HTMLDivElement>(null)
  const currentMapRef = useRef<any>(null)
  const [map, setMap] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [isLocating, setIsLocating] = useState(false)
  const [isMapRevealTransition, setIsMapRevealTransition] = useState(false)
  const [error, setError] = useState('')
  const [markers, setMarkers] = useState<any[]>([])
  const [initialized, setInitialized] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const knownSpotsRef = useRef<Map<string, { spot: ParkingSpot; marker: any }>>(new Map())
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null)
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [locationMethod, setLocationMethod] = useState<string>('')
  const [userLocationMarker, setUserLocationMarker] = useState<any>(null)
  const [parkedVehicleMarker, setParkedVehicleMarker] = useState<any>(null)
  const [selectedLocationMarker, setSelectedLocationMarker] = useState<any>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const revealTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const fallback = getFallbackLocation()
  const defaultLocation = { latitude: fallback.latitude, longitude: fallback.longitude }

  const getLocationErrorMessage = (error: any): string => {
    if (error instanceof GeolocationError) {
      switch (error.code) {
        case 'PERMISSION_DENIED':
          return t('map.permissionDenied') || 'Location permission denied. Please allow location access in Safari settings.'
        case 'TIMEOUT':
          return t('map.locationTimeout') || 'Location request timed out. Please try again.'
        case 'POSITION_UNAVAILABLE':
          return t('map.locationUnavailable') || 'Unable to determine your location right now.'
        case 'INSECURE_CONTEXT':
          return t('map.secureContextRequired') || 'Location requires HTTPS on iPhone/Safari.'
        case 'UNSUPPORTED':
          return t('map.locationUnsupported') || 'Geolocation is not supported on this browser.'
        default:
          return error.message || t('map.locationError') || 'Location error.'
      }
    }

    return error?.message || t('map.locationError') || 'Location error.'
  }

  const centerMapOnLocation = (mapInstance: any, location: UserLocation) => {
    try {
      if (mapInstance.setCenter) {
        // Google Maps
        mapInstance.setCenter({ lat: location.latitude, lng: location.longitude })
      } else if (mapInstance.setView) {
        // Leaflet
        if (typeof mapInstance.invalidateSize === 'function') {
          mapInstance.invalidateSize()
        }
        mapInstance.setView([location.latitude, location.longitude])
      }
    } catch (error) {
      console.warn('Could not center map:', error)

      // Retry léger pour Leaflet quand la map finit son layout
      if (mapInstance?.setView) {
        setTimeout(() => {
          try {
            if (typeof mapInstance.invalidateSize === 'function') {
              mapInstance.invalidateSize()
            }
            mapInstance.setView([location.latitude, location.longitude])
          } catch (retryError) {
            console.warn('Could not center map on retry:', retryError)
          }
        }, 120)
      }
    }
  }

  const isCurrentMapInstance = (mapInstance: any) => {
    return !!mapInstance && currentMapRef.current === mapInstance
  }

  const attachMapEventListeners = (mapInstance: any) => {
    const debouncedSearch = () => {
      if (!isCurrentMapInstance(mapInstance)) return

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      searchTimeoutRef.current = setTimeout(() => {
        if (!isCurrentMapInstance(mapInstance)) return
        searchParkingsInCurrentView(mapInstance, true)
      }, 1000)
    }

    try {
      if (mapInstance.addListener) {
        // Google Maps
        mapInstance.addListener('idle', debouncedSearch)
        mapInstance.addListener('bounds_changed', () => {
          if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current)
          }
        })
        mapInstance.addListener('click', (event: any) => {
          const clickedLocation = {
            latitude: event?.latLng?.lat?.() ?? event?.latLng?.lat,
            longitude: event?.latLng?.lng?.() ?? event?.latLng?.lng,
          }

          if (
            typeof clickedLocation.latitude === 'number' &&
            typeof clickedLocation.longitude === 'number'
          ) {
            handleMapClick(mapInstance, clickedLocation)
          }
        })
      } else if (mapInstance.on) {
        // Leaflet
        mapInstance.on('moveend', debouncedSearch)
        mapInstance.on('zoomend', debouncedSearch)
        mapInstance.on('move', () => {
          if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current)
          }
        })
        mapInstance.on('click', (event: any) => {
          const clickedLocation = {
            latitude: event?.latlng?.lat,
            longitude: event?.latlng?.lng,
          }

          if (
            typeof clickedLocation.latitude === 'number' &&
            typeof clickedLocation.longitude === 'number'
          ) {
            handleMapClick(mapInstance, clickedLocation)
          }
        })
      }
    } catch (error) {
      console.warn('Could not add map event listeners:', error)
    }
  }

  const clearSelectedLocation = () => {
    if (selectedLocationMarker) {
      mapsService.removeMarker(selectedLocationMarker)
      setSelectedLocationMarker(null)
    }

    if (onMapClick) {
      onMapClick(null)
    }
  }

  const handleMapClick = (mapInstance: any, location: UserLocation) => {
    if (!isCurrentMapInstance(mapInstance)) return

    if (selectedLocationMarker) {
      mapsService.removeMarker(selectedLocationMarker)
    }

    try {
      const marker = mapsService.addMarker(
        mapInstance,
        location,
        t('dashboard.selectedLocation') || 'Selected location',
        false,
        'selected'
      )
      setSelectedLocationMarker(marker)
    } catch (error) {
      console.warn('Could not add selected location marker:', error)
    }

    if (onMapClick) {
      onMapClick(location)
    }
  }

  const updateUserLocationMarker = (mapInstance: any, location: UserLocation) => {
    if (!isCurrentMapInstance(mapInstance)) {
      return
    }

    if (userLocationMarker) {
      mapsService.removeMarker(userLocationMarker)
    }

    try {
      const marker = mapsService.addMarker(mapInstance, location, t('map.yourLocation'), true)
      if (isCurrentMapInstance(mapInstance)) {
        setUserLocationMarker(marker)
      }
    } catch (error) {
      console.warn('Could not update user location marker:', error)
    }
  }

  const applyResolvedLocation = async (
    mapInstance: any,
    location: UserLocation & { method?: string },
    options?: { refreshed?: boolean }
  ) => {
    if (!isCurrentMapInstance(mapInstance)) return

    setError('')
    setUserLocation(location)
    setLocationMethod(
      options?.refreshed
        ? `${location.method || 'GPS/Network'} (Refreshed)`
        : (location.method || 'GPS/Network')
    )

    centerMapOnLocation(mapInstance, location)
    updateUserLocationMarker(mapInstance, location)

    // Dès que la localisation est obtenue, on retire l'animation
    // (la recherche de parkings peut continuer en arrière-plan)
    setIsMapRevealTransition(true)
    setIsLocating(false)
    if (revealTimeoutRef.current) clearTimeout(revealTimeoutRef.current)
    revealTimeoutRef.current = setTimeout(() => {
      setIsMapRevealTransition(false)
    }, 900)

    // Reset des parkings connus (nouvelle position = nouvelle recherche)
    const known = knownSpotsRef.current
    const oldMarkers = Array.from(known.values()).map(e => e.marker)
    mapsService.clearMarkers(oldMarkers)
    known.clear()
    setMarkers([])

    // Search for parkings with the new location
    await searchParkingsInCurrentView(mapInstance, true, location)
  }

  const searchParkingsInCurrentView = async (
    mapInstance: any,
    showLoading: boolean = false,
    locationOverride?: UserLocation,
    allowWithoutUserLocation: boolean = false
  ) => {
    if (isSearching || !isCurrentMapInstance(mapInstance)) return

    let centerLocation: UserLocation | undefined
    if (!locationOverride) {
      try {
        const center = mapInstance?.getCenter?.()
        if (center) {
          // wrap() normalise la longitude entre -180 et 180 (Leaflet peut dépasser en scrollant)
          const wrapped = typeof center.wrap === 'function' ? center.wrap() : center
          centerLocation = {
            latitude: typeof wrapped.lat === 'function' ? wrapped.lat() : wrapped.lat,
            longitude: typeof wrapped.lng === 'function' ? wrapped.lng() : wrapped.lng,
          }
        }
      } catch (_) {
        centerLocation = undefined
      }
    }

    const effectiveLocation = locationOverride || userLocation || centerLocation || undefined
    if (!effectiveLocation && !allowWithoutUserLocation) {
      return
    }

    try {
      if (showLoading) setIsSearching(true)

      // Log premium map search
      await logPremiumMapUsage('search_parkings', {
        location: effectiveLocation || { latitude: 0, longitude: 0, method: 'unknown' },
        showLoading,
        bounds: mapInstance?.getBounds?.() || 'unknown'
      })

      const newSpots = await mapsService.searchParkingsInBounds(mapInstance, effectiveLocation)
      console.log(`[Map] searchParkingsInBounds returned ${newSpots.length} spots`, newSpots.length > 0 ? { first: newSpots[0].place_id, lat: newSpots[0].latitude, lng: newSpots[0].longitude } : '')
      if (!isCurrentMapInstance(mapInstance)) {
        console.log('[Map] Map instance changed, discarding results')
        return
      }

      const known = knownSpotsRef.current
      let hasNew = false

      // Ajouter uniquement les nouveaux parkings (pas déjà connus)
      for (const spot of newSpots) {
        if (known.has(spot.place_id)) {
          // Mettre à jour la distance si la position utilisateur a changé
          const existing = known.get(spot.place_id)!
          existing.spot.distance = spot.distance
          continue
        }

        hasNew = true
        const marker = mapsService.addMarker(
          mapInstance,
          { latitude: spot.latitude, longitude: spot.longitude },
          spot.name
        )

        try {
          if (marker.addListener) {
            marker.addListener('click', () => {
              console.log('Parking spot clicked:', spot)
              if (onMarkerClick) onMarkerClick(spot)
            })
          } else if (marker.on) {
            marker.on('click', () => {
              console.log('Parking spot clicked:', spot)
              if (onMarkerClick) onMarkerClick(spot)
            })
          }
        } catch (error) {
          console.warn('Could not add click listener to marker:', error)
        }

        known.set(spot.place_id, { spot, marker })
      }

      console.log(`[Map] hasNew=${hasNew}, known spots=${known.size}`)
      // Notifier avec tous les parkings connus (triés par distance)
      if (hasNew || newSpots.length > 0) {
        const allSpots = Array.from(known.values())
          .map(entry => entry.spot)
          .sort((a, b) => (a.distance || 0) - (b.distance || 0))
        onParkingSpotsFound(allSpots)
      }
    } catch (error: any) {
      console.error('Error searching parkings:', error)
    } finally {
      if (showLoading) setIsSearching(false)
    }
  }

  const initializeMap = async () => {
    if (initialized) return
    
    try {
      setLoading(true)
      setError('')

      mapsService.cleanup()
      currentMapRef.current = null
      
      // Load Google Maps
      await mapsService.loadGoogleMaps()
      
      // Initialize map with default location first (no geolocation request yet)
      if (mapRef.current) {
        const mapInstance = await mapsService.initializeMap(mapRef.current, defaultLocation)
        currentMapRef.current = mapInstance
        setMap(mapInstance)

        let permissionGranted = false
        try {
          permissionGranted = await mapsService.requestLocationPermission()
        } catch (permissionError) {
          permissionGranted = false
        }

        if (permissionGranted) {
          setIsLocating(true)
          const location = await mapsService.getCurrentLocation()
          await applyResolvedLocation(mapInstance, location)
          setShowLocationModal(false)
          localStorage.setItem(LOCATION_PERMISSION_STORAGE_KEY, 'true')
        } else {
          const previouslyGranted = localStorage.getItem(LOCATION_PERMISSION_STORAGE_KEY) === 'true'

          if (previouslyGranted) {
            try {
              setIsLocating(true)
              const location = await mapsService.getCurrentLocation()
              await applyResolvedLocation(mapInstance, location)
              setShowLocationModal(false)
              localStorage.setItem(LOCATION_PERMISSION_STORAGE_KEY, 'true')
            } catch (autoLocationError) {
              localStorage.removeItem(LOCATION_PERMISSION_STORAGE_KEY)
              setLocationMethod('Awaiting permission')
              setShowLocationModal(true)
            }
          } else {
            setLocationMethod('Awaiting permission')
            setShowLocationModal(true)
          }
        }
        
        attachMapEventListeners(mapInstance)
        
        setInitialized(true)
      }
    } catch (error: any) {
      setError(getLocationErrorMessage(error) || 'Failed to initialize map')
    } finally {
      setIsLocating(false)
      setLoading(false)
    }
  }

  const handleLocationRequest = async () => {
    try {
      setLoading(true)
      setIsLocating(true)
      const location = await mapsService.getCurrentLocation()
      localStorage.setItem(LOCATION_PERMISSION_STORAGE_KEY, 'true')
      setShowLocationModal(false)
      
      if (map) {
        await applyResolvedLocation(map, location)
      }
    } catch (error: any) {
      setError(getLocationErrorMessage(error) || 'Failed to get location')
      if (error instanceof GeolocationError && error.code === 'PERMISSION_DENIED') {
        localStorage.removeItem(LOCATION_PERMISSION_STORAGE_KEY)
        setShowLocationModal(true)
      }
    } finally {
      setIsLocating(false)
      setLoading(false)
    }
  }

  const handleSkipLocation = async () => {
    setShowLocationModal(false)
    // Use default location
    setUserLocation(defaultLocation)
    setLocationMethod(fallback.method)

    if (userLocationMarker) {
      mapsService.removeMarker(userLocationMarker)
      setUserLocationMarker(null)
    }
    
    const mapInstance = currentMapRef.current || map
    if (!mapInstance) return

    centerMapOnLocation(mapInstance, defaultLocation)
    await searchParkingsInCurrentView(mapInstance, true, defaultLocation, true)
    setInitialized(true)
  }

  useEffect(() => {
    if (!initialized) {
      initializeMap()
    }
  }, [initialized])

  useEffect(() => {
    if (map && selectedSpot) {
      // Center map on selected parking spot (provider-agnostic)
      try {
        if (map.setCenter && map.setZoom) {
          // Google Maps
          map.setCenter({ lat: selectedSpot.latitude, lng: selectedSpot.longitude })
          map.setZoom(17)
        } else if (map.setView) {
          // Leaflet
          map.setView([selectedSpot.latitude, selectedSpot.longitude], 17)
        }
      } catch (error) {
        console.warn('Could not center map on selected spot:', error)
      }
    }
  }, [map, selectedSpot])

  // Ensure user GPS marker is present whenever map + location are available
  useEffect(() => {
    if (!map || !userLocation || !isCurrentMapInstance(map)) return
    updateUserLocationMarker(map, userLocation)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, userLocation])

  // Parked vehicle marker effect
  useEffect(() => {
    if (!map || !isCurrentMapInstance(map)) return

    // Remove existing marker if any
    if (parkedVehicleMarker) {
      mapsService.removeMarker(parkedVehicleMarker)
      setParkedVehicleMarker(null)
    }

    if (parkedVehicle) {
      try {
        const marker = mapsService.addMarker(
          map,
          { latitude: parkedVehicle.latitude, longitude: parkedVehicle.longitude },
          t('dashboard.parkedAt'),
          false,
          'parked'
        )
        if (isCurrentMapInstance(map)) {
          setParkedVehicleMarker(marker)
        }
      } catch (error) {
        console.warn('Could not add parked vehicle marker:', error)
      }
    }
  }, [map, parkedVehicle])

  useEffect(() => {
    if (selectedParkingLocation || !selectedLocationMarker) return

    mapsService.removeMarker(selectedLocationMarker)
    setSelectedLocationMarker(null)
  }, [selectedParkingLocation, selectedLocationMarker])

  // Cleanup markers effect
  useEffect(() => {
    return () => {
      // Clear timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
      
      // Clear markers using the service
      const known = knownSpotsRef.current
      if (known.size > 0) {
        const allMarkers = Array.from(known.values()).map(e => e.marker)
        mapsService.clearMarkers(allMarkers)
        known.clear()
      }
    }
  }, [markers])

  // Global cleanup (unmount only)
  useEffect(() => {
    return () => {
      if (revealTimeoutRef.current) {
        clearTimeout(revealTimeoutRef.current)
      }
      knownSpotsRef.current.clear()
      currentMapRef.current = null
      mapsService.cleanup()
    }
  }, [])

  const refreshLocation = async () => {
    if (!map) return
    
    try {
      setIsLocating(true)
      setLoading(true)
      clearSelectedLocation()
      const location = await mapsService.getCurrentLocation()
      localStorage.setItem(LOCATION_PERMISSION_STORAGE_KEY, 'true')
      await applyResolvedLocation(map, location, { refreshed: true })
    } catch (error: any) {
      setError(getLocationErrorMessage(error) || 'Failed to get location')
      if (error instanceof GeolocationError && error.code === 'PERMISSION_DENIED') {
        localStorage.removeItem(LOCATION_PERMISSION_STORAGE_KEY)
        setShowLocationModal(true)
      }
    } finally {
      setIsLocating(false)
      setLoading(false)
    }
  }

  return (
    <div className="relative h-full">
      <div ref={mapRef} className="w-full h-full" />
      
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-paybyphone-green mx-auto mb-2"></div>
            <p className="text-gray-600">{t('map.loadingMap')}</p>
          </div>
        </div>
      )}

      {isLocating && (
        <div className="absolute inset-0 z-[12000] bg-slate-900/60 backdrop-blur-md flex items-center justify-center overflow-hidden">
          {/* Subtle background glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] max-w-[600px] max-h-[600px] bg-emerald-500/20 rounded-full blur-[100px] animate-pulse"></div>
          
          <div className="relative flex flex-col items-center justify-center">
            {/* Radar / Ripple circles */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center mt-[-32px]">
              <div className="absolute w-[120px] h-[120px] rounded-full border border-emerald-400/40 animate-ping" style={{ animationDuration: '3s' }}></div>
              <div className="absolute w-[200px] h-[200px] rounded-full border border-emerald-400/20 animate-ping" style={{ animationDuration: '3s', animationDelay: '1s' }}></div>
              <div className="absolute w-[280px] h-[280px] rounded-full border border-emerald-400/10 animate-ping" style={{ animationDuration: '3s', animationDelay: '2s' }}></div>
            </div>

            {/* Main glassmorphism container */}
            <div className="relative z-10 w-24 h-24 rounded-3xl bg-white/10 backdrop-blur-2xl border border-white/20 shadow-[0_8px_32px_rgba(16,185,129,0.3)] flex items-center justify-center mb-8">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-white/5 to-white/20 opacity-50"></div>
              
              <div className="relative flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-tr from-emerald-400 to-teal-400 shadow-[0_0_20px_rgba(52,211,153,0.5)] animate-bounce" style={{ animationDuration: '2s' }}>
                <svg className="w-7 h-7 text-white drop-shadow-md" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </div>

            {/* Text indicator */}
            <div className="relative z-10 bg-white/10 backdrop-blur-xl px-6 py-3 rounded-2xl border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.2)]">
              <div className="flex items-center space-x-3">
                <div className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]"></span>
                </div>
                <span className="text-white font-medium tracking-wide text-sm sm:text-base drop-shadow-md">
                  {t('map.locatingPosition') || 'Recherche de votre position...'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {isMapRevealTransition && (
        <div className="pointer-events-none absolute inset-0 z-[11999] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-cyan-300/30 via-blue-200/20 to-transparent animate-pulse" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.65)_0%,rgba(255,255,255,0)_60%)] animate-pulse" />
          <div className="absolute inset-0 backdrop-blur-[1px] opacity-60" />
        </div>
      )}

      {error && (
        <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-[15000]">
          <div className="text-center p-6">
            <div className="text-red-500 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('map.mapError')}</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => {
                setInitialized(false)
                setError('')
              }}
              className="btn-primary"
            >
              {t('map.tryAgain')}
            </button>
          </div>
        </div>
      )}
      
      {/* Search indicator - Parking localization tooltip */}
      {isSearching && (
        <div className="absolute top-[88px] left-1/2 transform -translate-x-1/2 bg-emerald-600 text-white rounded-full shadow-lg px-5 py-2.5 flex items-center shadow-emerald-500/30" style={{ zIndex: 10001 }}>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2.5"></div>
          <span className="text-[13px] font-bold tracking-wide">{t('map.locatingParkingSpots')}</span>
        </div>
      )}
      
      {/* Location method indicator */}
      {locationMethod && (
        <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg px-3 py-2 z-[400]">
          <span className="text-xs text-gray-600">{t('map.location')}: {locationMethod}</span>
        </div>
      )}
      
      {/* Find Parking button */}
      <button
        onClick={refreshLocation}
        disabled={loading}
        className="absolute top-[88px] right-4 bg-white/95 backdrop-blur-md rounded-2xl shadow-lg border border-slate-100 p-3 hover:bg-slate-50 disabled:opacity-50 flex items-center justify-center transition-all active:scale-95"
        title={t('map.returnToMyLocation') || 'Return to my location'}
        style={{ zIndex: 400 }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
        </svg>
      </button>

      {/* Location Permission Modal */}
      <LocationPermissionModal
        isOpen={showLocationModal}
        onRequestLocation={handleLocationRequest}
        onClose={() => setShowLocationModal(false)}
        onSkip={handleSkipLocation}
      />
    </div>
  )
}
